import { useCallback } from "react"
import { Modal } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { serialize } from "object-to-formdata"
import { useWizardContext } from "./WizardContext"
import { STEP_FIELDS, getSteps } from "./constants/stepFields"
import { updateTrip } from "store/trips"

/**
 * Core wizard logic: validate → save → navigate.
 *
 * Usage: call inside any component rendered within WizardProvider.
 *
 * Steps with non-form local state (scheduling, media, location, resources, digital_waiver)
 * register async save functions in stepSaveHandlersRef. Steps that only use form fields
 * (details, preview) have their handlers registered by the step component directly.
 */
export const useExperienceWizard = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id: tripId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { form, stepSaveHandlersRef, isSaving, setIsSaving } = useWizardContext()

  const steps = getSteps()
  const currentStep = searchParams.get("step") || steps[0]
  const currentIndex = steps.indexOf(currentStep) === -1 ? 0 : steps.indexOf(currentStep)

  const validateStep = useCallback(
    async (stepId) => {
      const fields = STEP_FIELDS[stepId] || []
      if (fields.length === 0) return
      await form.validateFields(fields)
    },
    [form]
  )

  const saveStep = useCallback(
    async (stepId) => {
      const handler = stepSaveHandlersRef.current[stepId]
      if (handler) {
        await handler()
      }
      // Steps without a registered handler have nothing to save (e.g. preview reads trip directly)
    },
    [stepSaveHandlersRef]
  )

  const navigateToStep = useCallback(
    (stepId) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set("step", stepId)
        return next
      })
    },
    [setSearchParams]
  )

  const handleNext = useCallback(async () => {
    try {
      setIsSaving(true)
      await validateStep(currentStep)
      await saveStep(currentStep)
      if (currentIndex < steps.length - 1) {
        navigateToStep(steps[currentIndex + 1])
      }
    } catch (err) {
      // Validation errors surface as AntD field errors — no additional handling needed
      console.error("[Wizard] Next step failed:", err)
    } finally {
      setIsSaving(false)
    }
  }, [currentStep, currentIndex, steps, validateStep, saveStep, navigateToStep])

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      navigateToStep(steps[currentIndex - 1])
    }
  }, [currentIndex, steps, navigateToStep])

  /**
   * Tab click: validate + save current step, then navigate.
   * On validation failure: shows inline AntD field errors AND a Modal.error
   * listing the missing fields — navigation is blocked. Matches old module behavior.
   */
  const handleStepClick = useCallback(
    async (stepId) => {
      if (stepId === currentStep) return
      try {
        setIsSaving(true)
        await validateStep(currentStep)
        await saveStep(currentStep)
        navigateToStep(stepId)
      } catch (err) {
        if (err?.errorFields?.length) {
          Modal.error({
            title: t("experiences.validation.incomplete_section"),
            content: (
              <div>
                <p>{t("experiences.validation.missing_fields_message")}</p>
                <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
                  {err.errorFields.map(({ errors }, i) => (
                    <li key={i}>{errors[0]}</li>
                  ))}
                </ul>
                <p style={{ marginTop: "12px" }}>
                  {t("experiences.validation.please_complete_required_fields")}
                </p>
              </div>
            ),
            okText: t("global.ok")
          })
        } else {
          console.error("[Wizard] Tab switch failed:", err)
        }
      } finally {
        setIsSaving(false)
      }
    },
    [currentStep, validateStep, saveStep, navigateToStep, t]
  )

  /**
   * Save current step without navigating; calls onSuccess on completion.
   */
  const handleSaveDraft = useCallback(
    async (onSuccess) => {
      try {
        setIsSaving(true)
        await saveStep(currentStep)
        onSuccess?.()
      } catch (err) {
        console.error("[Wizard] Save draft failed:", err)
      } finally {
        setIsSaving(false)
      }
    },
    [currentStep, saveStep]
  )

  /**
   * Publish the experience (last step action).
   */
  const handlePublish = useCallback(
    async (onSuccess) => {
      try {
        setIsSaving(true)
        const formData = serialize({ trip: { status: "published" } })
        await dispatch(updateTrip(tripId, formData))
        onSuccess?.()
      } catch (err) {
        console.error("[Wizard] Publish failed:", err)
      } finally {
        setIsSaving(false)
      }
    },
    [tripId, dispatch]
  )

  return {
    currentStep,
    currentIndex,
    steps,
    tripId,
    isSaving,
    isFirstStep: currentIndex === 0,
    isLastStep: currentIndex === steps.length - 1,
    handleNext,
    handlePrevious,
    handleStepClick,
    handleSaveDraft,
    handlePublish,
    navigateToStep,
    navigate
  }
}
